import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { listReferenceImages } from '../../api/referenceImages'
import {
  type LibraryCatalogState,
  type ReferenceImageItem,
} from '../../api/referenceImages'
import { useImageLightbox } from '../../context/ImageLightboxContext'
import { useToast } from '../../context/ToastContext'
import { useBlockingAction } from '../../hooks/useBlockingAction'
import { showApiErrorToast } from '../../lib/apiErrorToast'
import { isAbortError } from '../../lib/isAbortError'
import { LIBRARY_REFERENCE_UPLOAD_SEGMENT } from '../../lib/libraryPaths'
import type { PromptTemplate } from '../../lib/promptTemplateStore'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import {
  chromeDisabledControlClasses,
  chromeIconButtonClass,
  chromePrimaryClass,
  chromeTemplateChipClass,
} from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { FloatingRemoveBubble } from '../FloatingRemoveBubble'
import { HorizontalChipStrip } from '../HorizontalChipStrip'
import { BlockingModal } from '../modal'
import { IconOutputSuperResolution, IconTemplateManage, IconUploadSmall } from '../nav-icons'
import {
  LibraryReferencePickerModal,
  type LibraryReferencePickerTab,
  referencePickerSubmitUrl,
} from '../reference/LibraryReferencePickerModal'
import { ThemedImage } from '../ThemedImage'

type PromptComposerSectionProps = {
  angular: boolean
  /** 展开时显示模板、输入框与生成按钮 */
  expanded: boolean
  onToggleExpanded: () => void
  prompt: string
  onPromptChange: (value: string) => void
  templates: PromptTemplate[]
  activeTemplateIds: string[]
  onToggleTemplateChip: (t: PromptTemplate) => void
  onOpenTemplateModal: () => void
  isGenerating: boolean
  /** 为 false 时禁用生成（未成功拉取服务端模型/尺寸清单） */
  platformCatalogReady?: boolean
  /** 为 false 时禁用提交类操作（生成、参考图、模板管理） */
  canSubmit?: boolean
  timelineHydrated: boolean
  /** 当前所选模型是否支持把参考图交给多米生图（含 gpt-image-2 与香蕉） */
  referenceImagesSupported: boolean
  referenceUploadBusy: boolean
  referenceAbsoluteUrls: string[]
  referenceCount: number
  referenceMax: number
  onReferenceFiles: (files: FileList | readonly File[] | null) => void
  onReferenceLibraryUrls: (urls: readonly string[]) => void
  onRemoveReference: (index: number) => void
  libraryCatalog?: LibraryCatalogState | null
  libraryCatalogLoading?: boolean
  /** 后端下发：生成结果超清能力可用 */
  referenceSuperResolutionAvailable?: boolean
  /** 是否在生成完成后对输出图做多米超清 */
  outputSuperResolution?: boolean
  onOutputSuperResolutionChange?: (next: boolean) => void
  /** 图库记录变更后刷新（如删除损坏条目） */
  onRefreshLibraryCatalog?: () => void | Promise<void>
  /** 即将打开图库选择器时请求加载目录（按需拉取） */
  onRequestLibraryCatalog?: () => void
}

/** expanded=true → 朝下（收起）；expanded=false → 朝上（展开） */
function ChevronToggleIcon({ expanded: open, className }: { expanded: boolean; className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {open ? <path d="M6 9l6 6 6-6" /> : <path d="M18 15l-6-6-6 6" />}
    </svg>
  )
}

function ReferenceStackDock({
  angular,
  open,
  count,
  urls,
  onToggle,
  onPreviewReference,
  onRemoveReference,
}: {
  angular: boolean
  open: boolean
  count: number
  urls: string[]
  onToggle: () => void
  onPreviewReference: (index: number) => void
  onRemoveReference: (index: number) => void
}) {
  const cardRound = angular ? 'rounded-none' : 'rounded-lg'
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(motionPreset, { reducedMotion: !!reducedMotion })

  const panelChrome = (
    <div
      className={[
        'min-w-0 w-full overflow-hidden border border-line bg-[color-mix(in_srgb,var(--surface-muted)_75%,transparent)] p-2',
        cardRound,
      ].join(' ')}
    >
      <HorizontalChipStrip className="px-1 py-2" trackClassName="gap-2">
        {urls.map((u, idx) => (
          <div key={`${u}-${idx}`} className="relative isolate shrink-0">
            <button
              type="button"
              onClick={() => onPreviewReference(idx)}
              className="relative block overflow-hidden"
              aria-label={`预览参考图 ${idx + 1}`}
            >
              <ThemedImage
                src={u}
                alt=""
                angular={angular}
                compact
                interactiveSave
                downloadNameHint={`composer_ref_${idx + 1}`}
                className={[
                  'h-16 w-24 border border-line bg-surface-muted object-cover transition hover:border-accent',
                  angular ? 'rounded-none' : 'rounded-md',
                ].join(' ')}
              />
            </button>
            <FloatingRemoveBubble
              angular={angular}
              tooltipContent="移除该参考图"
              className="pointer-events-auto absolute right-1 top-1 z-10"
              aria-label={`删除参考图 ${idx + 1}`}
              onClick={(e) => {
                e.stopPropagation()
                onRemoveReference(idx)
              }}
            />
          </div>
        ))}
      </HorizontalChipStrip>
    </div>
  )

  return (
    <div className="min-w-0 w-full shrink-0">
      <button
        type="button"
        onClick={onToggle}
        className={[
          'w-full border border-line bg-panel px-3 py-2 text-left transition hover:border-accent hover:bg-surface-muted',
          cardRound,
        ].join(' ')}
        aria-expanded={open}
        aria-label={open ? '收起参考图面板' : '展开参考图面板'}
      >
        <span className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span className="relative h-8 w-14 shrink-0" aria-hidden>
              {urls.slice(0, 3).map((u, i) => (
                <ThemedImage
                  key={`${u}-${i}`}
                  src={u}
                  alt=""
                  angular={angular}
                  compact
                  className={[
                    'absolute top-0 h-8 w-10 border border-line bg-surface-muted object-cover shadow-sm',
                    angular ? 'rounded-none' : 'rounded-md',
                  ].join(' ')}
                  style={{ left: `${i * 10}px`, zIndex: 3 - i, transform: `rotate(${(i - 1) * 5}deg)` }}
                />
              ))}
            </span>
            <span className="min-w-0 truncate text-xs font-semibold text-ink">
              参考图 {count} 张
            </span>
          </span>
          <ChevronToggleIcon expanded={open} className="h-4 w-4 text-ink-soft" />
        </span>
      </button>

      {reducedMotion ? (
        open ? (
          <div key="ref-panel" className="mt-2 overflow-hidden">
            {panelChrome}
          </div>
        ) : null
      ) : (
        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              key="ref-panel"
              initial={{ height: 0, opacity: 0, y: -4 }}
              animate={{ height: 'auto', opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -4 }}
              transition={expandTrans}
              className="mt-2 overflow-hidden"
            >
              {panelChrome}
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    </div>
  )
}

/**
 * 底部固定：提示词标题、模板 chips、输入框、生成；支持整区收起
 */
export function PromptComposerSection({
  angular,
  expanded,
  onToggleExpanded,
  prompt,
  onPromptChange,
  templates,
  activeTemplateIds,
  onToggleTemplateChip,
  onOpenTemplateModal,
  isGenerating: _isGenerating,
  platformCatalogReady = true,
  canSubmit = true,
  timelineHydrated,
  referenceImagesSupported,
  referenceUploadBusy,
  referenceAbsoluteUrls,
  referenceCount,
  referenceMax,
  onReferenceFiles,
  onReferenceLibraryUrls,
  onRemoveReference,
  libraryCatalog,
  libraryCatalogLoading = false,
  referenceSuperResolutionAvailable = false,
  outputSuperResolution = false,
  onOutputSuperResolutionChange,
  onRefreshLibraryCatalog,
  onRequestLibraryCatalog,
}: PromptComposerSectionProps) {
  const { blockingState, runBlocking } = useBlockingAction()
  const motionPreset = useThemeMotion()
  const reducedMotionDock = useReducedMotion()
  const dockPreset = mergeReducedMotionPreset(motionPreset, !!reducedMotionDock)
  const expandTrans = resolveExpandTransition(dockPreset, { reducedMotion: !!reducedMotionDock })
  const refInputRef = useRef<HTMLInputElement>(null)
  const [refPanelOpen, setRefPanelOpen] = useState(false)
  // 灯箱上提到路由之上：切换页面时预览状态不丢
  const imageLightbox = useImageLightbox()
  const { showToast } = useToast()
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false)
  const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([])
  /** 选择器分区：默认「上传记录」（时间倒序），图库按需加载 */
  const [pickerTab, setPickerTab] = useState<LibraryReferencePickerTab>('recent')
  const [recentItems, setRecentItems] = useState<ReferenceImageItem[]>([])
  const [recentLoading, setRecentLoading] = useState(false)
  const [recentTotal, setRecentTotal] = useState(0)
  const recentPageRef = useRef(0)
  const recentLoadingMoreRef = useRef(false)
  const uploadDisabled =
    !canSubmit || !referenceImagesSupported || referenceUploadBusy || referenceCount >= referenceMax
  const remainingReferenceSlots = Math.max(0, referenceMax - referenceCount)
  const libraryItems = useMemo(
    () => (libraryCatalog?.items ?? []).filter((item) => item.url.trim()),
    [libraryCatalog?.items],
  )

  /**
   * 必须在本机上传按钮的同步 click 链路里触发：经 async/await（含 async 函数首段隐式 Promise）
   * 后会话失去「用户激活」，Chrome 等对程序化 `input[type=file].click()` 直接静默失败，表现为点了没反应。
   */
  const triggerNativeReferenceFilePicker = () => {
    if (uploadDisabled) return
    refInputRef.current?.click()
  }

  /**
   * 按页拉取「上传记录」（本地磁盘「上传的参考图」子树，后端按 created_at 倒序）。
   * 图库目录仍由父级按需加载（onRequestLibraryCatalog），两个分区数据分源。
   */
  const loadRecentPage = useCallback(async (reset: boolean) => {
    const nextPage = reset ? 1 : recentPageRef.current + 1
    if (reset) {
      recentPageRef.current = 0
      setRecentItems([])
      setRecentTotal(0)
    }
    setRecentLoading(true)
    try {
      const res = await listReferenceImages(nextPage, 60, undefined, LIBRARY_REFERENCE_UPLOAD_SEGMENT)
      const usable = res.items.filter((item) => item.url.trim())
      recentPageRef.current = nextPage
      setRecentTotal(res.total)
      setRecentItems((prev) => {
        if (reset) return usable
        const seen = new Set(prev.map((item) => item.publicId))
        return [...prev, ...usable.filter((item) => !seen.has(item.publicId))]
      })
    } catch (e) {
      if (!isAbortError(e)) {
        showApiErrorToast(showToast, e, '列表加载失败', 'error')
      }
      if (reset) setRecentItems([])
    } finally {
      setRecentLoading(false)
      recentLoadingMoreRef.current = false
    }
  }, [showToast])

  const openRefPicker = () => {
    if (uploadDisabled) return
    setSelectedLibraryIds([])
    setPickerTab('recent')
    setLibraryPickerOpen(true)
    void loadRecentPage(true)
  }

  const handlePickerTabChange = useCallback(
    (tab: LibraryReferencePickerTab) => {
      setPickerTab(tab)
      if (tab === 'gallery') {
        // 图库目录首次切到该分区时才拉取（按需加载，避免打开选择器就全量拉目录）
        if (!libraryCatalog) onRequestLibraryCatalog?.()
        return
      }
      if (recentPageRef.current === 0) void loadRecentPage(true)
    },
    [libraryCatalog, loadRecentPage, onRequestLibraryCatalog],
  )

  const handleRecentLoadMore = useCallback(() => {
    if (recentLoadingMoreRef.current) return
    if (recentItems.length >= recentTotal) return
    recentLoadingMoreRef.current = true
    void loadRecentPage(false)
  }, [loadRecentPage, recentItems.length, recentTotal])

  const toggleLibraryReference = (item: ReferenceImageItem) => {
    setSelectedLibraryIds((prev) => {
      if (prev.includes(item.publicId)) {
        return prev.filter((id) => id !== item.publicId)
      }
      if (prev.length >= remainingReferenceSlots) return prev
      return [...prev, item.publicId]
    })
  }

  const confirmLibraryReferences = () => {
    const byId = new Map(
      [...(libraryCatalog?.items ?? []), ...recentItems].map((item) => [item.publicId, item]),
    )
    const urls = selectedLibraryIds
      .map((id) => byId.get(id))
      .filter((item): item is ReferenceImageItem => Boolean(item))
      .map((item) =>
        referencePickerSubmitUrl(item.url, { bucket: item.bucket, objectKey: item.objectKey }),
      )
    onReferenceLibraryUrls(urls)
    setSelectedLibraryIds([])
    setLibraryPickerOpen(false)
  }

  const onRefInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onReferenceFiles(e.target.files)
    e.target.value = ''
  }

  const composerBodyId = 'create-prompt-composer-body'
  const uploadTip =
    !referenceImagesSupported
      ? '当前模型不支持参考图'
      : referenceUploadBusy
        ? '上传中…'
        : referenceCount >= referenceMax
          ? `最多 ${referenceMax} 张参考图`
          : '从图库选择参考图'

  const toggleReferencePanel = () => setRefPanelOpen((v) => !v)

  const openReferencePreview = (idx: number) => {
    if (idx < 0 || idx >= referenceAbsoluteUrls.length) return
    imageLightbox.open(
      referenceAbsoluteUrls.map((src, i) => ({
        src,
        taskLabel: `参考图 ${i + 1}/${referenceAbsoluteUrls.length}`,
        subtitle: '创作面板已上传参考图',
      })),
      { index: idx },
    )
  }

  const removeReferenceAt = (idx: number) => {
    imageLightbox.close()
    onRemoveReference(idx)
  }

  const dockEase = [...dockPreset.easing] as const

  /**
   * 长提示词默认收起：插入模板、粘贴长文后输入区只显示约 3 行，避免整块撑满页面。
   * 只在「由短变长」的瞬间折叠一次，用户手动展开后不再被自动打断；清空后复位。
   */
  const PROMPT_FOLD_THRESHOLD = 140
  const [promptFoldExpanded, setPromptFoldExpanded] = useState(false)
  const promptIsLong = prompt.trim().length > PROMPT_FOLD_THRESHOLD
  const promptWasLongRef = useRef(false)
  useEffect(() => {
    if (promptIsLong && !promptWasLongRef.current) setPromptFoldExpanded(false)
    if (!promptIsLong) setPromptFoldExpanded(false)
    promptWasLongRef.current = promptIsLong
  }, [promptIsLong])
  const promptFolded = promptIsLong && !promptFoldExpanded

  const expandedContent = (
    <>
      <div className="grid min-h-9 min-w-0 max-w-full shrink-0 grid-cols-[minmax(0,1fr)_auto] items-stretch gap-x-3 sm:gap-x-4">
        <HorizontalChipStrip
          className="composer-template-chip-scroll py-1 pl-0.5"
          trackClassName="items-center gap-1.5"
          aria-label="提示词模板列表"
        >
          {templates.length === 0 ? (
            <span className="shrink-0 whitespace-nowrap px-0.5 text-xs text-ink-soft">暂无模板，右侧按钮可管理</span>
          ) : (
            templates.map((t) => {
              const on = activeTemplateIds.includes(t.id)
              return (
                <ChromeTooltip key={t.id} content={t.body} angular={angular} side="bottom" align="center" className="shrink-0">
                  <button
                    type="button"
                    onClick={() => onToggleTemplateChip(t)}
                    className={chromeTemplateChipClass(angular, on)}
                    aria-pressed={on}
                  >
                    {t.name}
                  </button>
                </ChromeTooltip>
              )
            })
          )}
        </HorizontalChipStrip>
        <div className="flex h-full min-h-9 w-11 shrink-0 items-center justify-center border-l border-line bg-transparent sm:w-12">
          <ChromeTooltip content="提示词模板" angular={angular} side="top" align="center" className="shrink-0">
            <button
              type="button"
              onClick={onOpenTemplateModal}
              disabled={!canSubmit}
              className={[chromeIconButtonClass(angular), 'shadow-none hover:shadow-none'].join(' ')}
              aria-label="提示词模板"
            >
              <IconTemplateManage className="h-4 w-4 shrink-0" />
            </button>
          </ChromeTooltip>
        </div>
      </div>
      <ChromeComposerTextarea
        id="create-prompt"
        angular={angular}
        variant="composer"
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder="用自然语言描述画面…"
        className={[
          'max-lg:min-h-0 max-lg:flex-1',
          promptFolded ? 'lg:min-h-0' : 'lg:min-h-[6.75rem]',
        ].join(' ')}
        textareaClassName={promptFolded ? 'max-h-[5.25rem]' : 'max-h-none'}
      />
      {promptIsLong ? (
        <button
          type="button"
          onClick={() => setPromptFoldExpanded((v) => !v)}
          className="shrink-0 self-end text-xs font-medium text-accent hover:underline"
        >
          {promptFoldExpanded ? '收起提示词' : '展开全部提示词'}
        </button>
      ) : null}
      {referenceAbsoluteUrls.length > 0 ? (
        <ReferenceStackDock
          angular={angular}
          open={refPanelOpen}
          count={referenceCount}
          urls={referenceAbsoluteUrls}
          onToggle={toggleReferencePanel}
          onPreviewReference={openReferencePreview}
          onRemoveReference={removeReferenceAt}
        />
      ) : null}
      <button
        type="submit"
        disabled={!canSubmit || referenceUploadBusy || !timelineHydrated || !platformCatalogReady || !prompt.trim()}
        className={[chromePrimaryClass(angular, { block: true }), 'shrink-0'].join(' ')}
      >
        {referenceUploadBusy
          ? '上传参考图中…'
          : !canSubmit
            ? '无提交权限'
            : !platformCatalogReady
              ? '等待模型配置…'
              : !timelineHydrated
                ? '加载时间线…'
                : !prompt.trim()
                  ? '请输入提示词'
                  : '生成'}
      </button>
    </>
  )

  const collapsedContent =
    referenceAbsoluteUrls.length > 0 ? (
      <ReferenceStackDock
        angular={angular}
        open={refPanelOpen}
        count={referenceCount}
        urls={referenceAbsoluteUrls}
        onToggle={toggleReferencePanel}
        onPreviewReference={openReferencePreview}
        onRemoveReference={removeReferenceAt}
      />
    ) : null

  /** 无参考图时收起区仅余 padding，exit 若跟主题 expand 时长会拖住 mode="wait"；无布局则瞬时退场 */
  const collapsedHasLayout = referenceAbsoluteUrls.length > 0

  return (
    <motion.div
      className="create-prompt-composer-dock flex min-h-0 w-full min-w-0 max-w-full shrink-0 flex-col"
      initial={{ opacity: 0, y: reducedMotionDock ? 0 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: dockPreset.duration, ease: dockEase }}
    >
      <input
        ref={refInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="sr-only"
        disabled={uploadDisabled}
        onChange={onRefInputChange}
      />
      <div
        className={[
          'flex min-w-0 items-center justify-end gap-x-2 px-3 pb-2 pt-2 sm:px-5 sm:pb-2 sm:pt-3.5',
          expanded ? 'border-b border-line' : '',
        ].join(' ')}
      >
        <div className="flex min-w-0 flex-wrap justify-end gap-x-2 gap-y-2 sm:flex-nowrap">
          <ChromeTooltip content={uploadTip} angular={angular} side="top" align="end">
            <button
              type="button"
              onClick={openRefPicker}
              disabled={uploadDisabled}
              className={[chromeIconButtonClass(angular), 'shadow-none hover:shadow-none'].join(' ')}
              aria-label="从图库选择参考图"
            >
              <IconUploadSmall className="h-4 w-4 shrink-0" />
            </button>
          </ChromeTooltip>
          {referenceSuperResolutionAvailable ? (
            <ChromeTooltip
              content={outputSuperResolution ? '关闭超清' : '开启超清'}
              angular={angular}
              side="top"
              align="end"
            >
              <button
                type="button"
                role="switch"
                aria-checked={outputSuperResolution}
                aria-label={outputSuperResolution ? '关闭超清' : '开启超清'}
                onClick={() => onOutputSuperResolutionChange?.(!outputSuperResolution)}
                className={
                  outputSuperResolution
                    ? [
                        'inline-flex size-9 shrink-0 items-center justify-center bg-accent text-white antialiased outline-none transition',
                        angular
                          ? 'rounded-none border-2 border-accent shadow-none'
                          : 'rounded-full border border-accent shadow-sm',
                        'hover:border-accent hover:bg-accent-hover hover:text-white focus-visible:ring-2 focus-visible:ring-accent',
                        chromeDisabledControlClasses,
                      ].join(' ')
                    : [chromeIconButtonClass(angular), 'shadow-none hover:shadow-none'].join(' ')
                }
              >
                <IconOutputSuperResolution className="h-4 w-4 shrink-0" />
              </button>
            </ChromeTooltip>
          ) : null}
          <ChromeTooltip content={expanded ? '收起' : '展开'} angular={angular} side="top" align="end">
            <button
              type="button"
              onClick={onToggleExpanded}
              className={[chromeIconButtonClass(angular), 'shadow-none hover:shadow-none'].join(' ')}
              aria-expanded={expanded}
              aria-controls={composerBodyId}
              aria-label={expanded ? '收起提示词区域' : '展开提示词区域'}
            >
              <ChevronToggleIcon expanded={expanded} className="h-4 w-4" />
            </button>
          </ChromeTooltip>
        </div>
      </div>

      <AnimatePresence initial={false} mode="wait">
        {expanded ? (
          reducedMotionDock ? (
            <div
              key="composer-open"
              id={composerBodyId}
              className="flex min-h-0 w-full min-w-0 max-w-full flex-col gap-2 overflow-x-hidden px-3 pb-2 pt-0 max-lg:max-h-[min(52vh,460px)] max-lg:overflow-y-hidden sm:gap-2.5 sm:px-5 sm:pb-4 lg:max-h-none lg:overflow-y-visible"
            >
              {expandedContent}
            </div>
          ) : (
            <motion.div
              key="composer-open"
              id={composerBodyId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={expandTrans}
              className="flex min-h-0 w-full min-w-0 max-w-full flex-col gap-2 overflow-x-hidden overflow-y-hidden px-3 pb-2 pt-0 max-lg:max-h-[min(52vh,460px)] sm:gap-2.5 sm:px-5 sm:pb-4 lg:max-h-none lg:overflow-y-visible"
            >
              {expandedContent}
            </motion.div>
          )
        ) : reducedMotionDock ? (
          <div
            key="composer-shut"
            id={composerBodyId}
            className="w-full min-w-0 max-w-full overflow-x-hidden px-3 pb-2 sm:px-5 sm:pb-4"
          >
            {collapsedContent}
          </div>
        ) : (
          <motion.div
            key="composer-shut"
            id={composerBodyId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
              transition: collapsedHasLayout ? expandTrans : { duration: 0 },
            }}
            transition={expandTrans}
            className="w-full min-w-0 max-w-full overflow-x-hidden overflow-y-hidden px-3 pb-2 sm:px-5 sm:pb-4"
          >
            {collapsedContent}
          </motion.div>
        )}
      </AnimatePresence>
      <LibraryReferencePickerModal
        angular={angular}
        open={libraryPickerOpen}
        loading={libraryCatalogLoading}
        items={libraryItems}
        activeTab={pickerTab}
        recentItems={recentItems}
        recentLoading={recentLoading}
        hasMore={recentItems.length < recentTotal}
        onTabChange={handlePickerTabChange}
        onLoadMore={handleRecentLoadMore}
        selectedIds={selectedLibraryIds}
        remaining={remainingReferenceSlots}
        referenceUrls={referenceAbsoluteUrls}
        selectionMode="multi"
        onClearSelection={() => setSelectedLibraryIds([])}
        title="选择参考图"
        onClose={() => {
          setLibraryPickerOpen(false)
          setSelectedLibraryIds([])
        }}
        onToggle={toggleLibraryReference}
        onUseLocalUpload={() => {
          triggerNativeReferenceFilePicker()
          setLibraryPickerOpen(false)
          setSelectedLibraryIds([])
        }}
        onConfirm={confirmLibraryReferences}
        onThumbBroken={(pid) =>
          setSelectedLibraryIds((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : prev))
        }
        onDeletedFromLibrary={(pid) => {
          setSelectedLibraryIds((prev) => prev.filter((x) => x !== pid))
          void onRefreshLibraryCatalog?.()
        }}
        runBlockingDelete={(action) =>
          runBlocking(action, { title: '删除中', message: '正在从图库删除该文件…' })
        }
      />
      <BlockingModal
        open={blockingState.open}
        title={blockingState.title}
        message={blockingState.message}
        detail={blockingState.detail}
      />
    </motion.div>
  )
}
